import { cn } from '@/lib/utils';
import { KeystoneMark } from '@/components/g5';

/**
 * "Aura" atmosphere + label bits. Soft radial glow, label chip, stat pill, and a
 * keystone divider. The keystone is the recurring brand mark across these.
 * Reads `--g5-*` color tokens; render inside `.ashlar-public`. Decorative pieces
 * are aria-hidden.
 */

/** A diffuse violet glow. Position absolutely; size via className. */
export function Glow({ className, strength = 0.2, style }: { className?: string; strength?: number; style?: React.CSSProperties }) {
  return (
    <div
      aria-hidden
      className={cn('pointer-events-none', className)}
      style={{
        background: `radial-gradient(closest-side, color-mix(in srgb, var(--g5-accent2) ${Math.round(strength * 100)}%, transparent), transparent)`,
        ...style,
      }}
    />
  );
}

/** Small outline label above a heading, e.g. ◆ Capabilities. Keystone bullet. */
export function LabelChip({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold', className)}
      style={{ border: '1px solid var(--g5-line)', color: 'var(--g5-accent)' }}
    >
      <KeystoneMark size={12} />
      {children}
    </span>
  );
}

/** A quiet stat pill: keystone bullet + bold value + muted label. */
export function StatPill({ value, label, className }: { value: string; label: string; className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center gap-2.5 rounded-full px-4 py-2', className)}
      style={{ background: 'var(--g5-card)', border: '1px solid var(--g5-line)' }}
    >
      <KeystoneMark size={13} />
      <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--g5-ink)' }}>{value}</span>
      <span className="text-xs" style={{ color: 'var(--g5-sub)' }}>{label}</span>
    </span>
  );
}

/**
 * A keystone divider — a hairline rule locked at the center by the keystone (the
 * springline of an arch). Use as an occasional section divider.
 */
export function CourseDivider({ className }: { className?: string }) {
  return (
    <div aria-hidden className={cn('flex items-center justify-center gap-3.5', className)}>
      <span className="h-px flex-1" style={{ maxWidth: 160, background: 'var(--g5-line)' }} />
      <KeystoneMark size={18} />
      <span className="h-px flex-1" style={{ maxWidth: 160, background: 'var(--g5-line)' }} />
    </div>
  );
}
